import { useViewPort } from "@website/hooks/viewport";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import type { FiltersSchemaType } from "@website/widget/filter/filter.types";
import cloneDeep from "lodash-es/cloneDeep";
import isEmpty from "lodash-es/isEmpty";
import mapValues from "lodash-es/mapValues";
import omit from "lodash-es/omit";
import pickBy from "lodash-es/pickBy";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";

type UseFiltersProperties = {
  schema: FiltersSchemaType;
};

export const useFilters = ({ schema }: UseFiltersProperties) => {
  const router = useRouter();

  const filtersSchemaEntries = Object.entries(schema);

  // eslint-disable-next-line unicorn/no-array-reduce
  const filterKeys = filtersSchemaEntries.reduce(
    (s: Array<string>, [name, schema]) =>
      schema.type === "range"
        ? [...s, schema.min_key, schema.max_key]
        : schema.type === "custom"
          ? [...s, ...schema.keys]
          : [...s, name],
    []
  );

  const selectedFiltersCount = filterKeys.filter((k) =>
    Boolean(router.query[k])
  ).length;

  function clearFilters() {
    router.replace(
      {
        query: omit(router.query, filterKeys)
      },
      undefined,
      { shallow: true, scroll: false }
    );
  }

  return { filterKeys, selectedFiltersCount, clearFilters };
};

export const useFilterLayoutEffect = ({
  dontSyncScroll
}: {
  dontSyncScroll?: boolean;
}) => {
  const formReference = useRef<HTMLDivElement>(null);
  const lastScrollPosition = useRef(0);
  const { isMobile } = useViewPort();

  useEffect(() => {
    if (!isMobile && formReference.current && !dontSyncScroll) {
      const syncScrollBar = () => {
        if (formReference.current) {
          formReference.current.scrollTop +=
            window.scrollY - lastScrollPosition.current;
          lastScrollPosition.current = window.scrollY;
        }
      };
      window.addEventListener("scroll", syncScrollBar);
      return () => window.removeEventListener("scroll", syncScrollBar);
    }
  }, [isMobile, dontSyncScroll]);

  return { formRef: formReference };
};

export function useCityFilterState() {
  const router = useRouter();

  const filterQueryName = "filters_visible";

  const visible = router.query[filterQueryName] === "true";

  const onOpenFilters = () =>
    router.push(
      {
        pathname: router.pathname,
        query: deepOmit(
          { [filterQueryName]: true, ...omit(router.query, filterQueryName) },
          isFilled
        )
      },
      undefined,
      { shallow: true, scroll: false }
    );

  const onCloseFilters = () =>
    router.replace(
      {
        pathname: router.pathname,
        query: deepOmit({ ...omit(router.query, filterQueryName) }, isFilled)
      },
      undefined,
      { shallow: true, scroll: false }
    );

  return { visible, onOpenFilters, onCloseFilters };
}

export function useFilterTracking({
  callback,
  schema
}: {
  callback?: ({ removed }: { removed: object }) => void;
  schema: FiltersSchemaType;
}) {
  const router = useRouter();
  const filtersStorage = useRef<Record<string, any>>({});

  useEffect(() => {
    if (!callback) return;

    const previous = filtersStorage.current;
    const current = mapValues(schema, (_, key) => router.query[key]);

    const removed = pickBy(
      previous,
      (_v, key) => current[key] === undefined && _v !== undefined
    );

    filtersStorage.current = cloneDeep(current);
    if (!isEmpty(removed)) {
      callback({ removed });
    }
  }, [router.query, callback]);
}
